import { toRefs, ref, computed, watch, nextTick, onMounted } from 'vue';
import debounce from 'lodash/debounce';
import { useIntersectionObserver } from '@vueuse/core';
import tinycolor from 'tinycolor2';
import uniqueId from 'lodash/uniqueId';
import { getCSSVariableName, PROFILE_PREFIX } from '../../../styles/component-lib.esm3.js';
import { Easing } from '../../../animations/component-lib.esm.js';
import { getElementHeight } from '../../../utils/component-lib.esm3.js';
import { useBtnColors } from '../../compositions/component-lib.esm2.js';
import { EVENTS } from '../../../config/component-lib.esm.js';
import { useResizeObserver } from '../../../compositions/component-lib.esm7.js';

//
var script = {
  props: {
    feedOptions: {
      type: Object,
      required: true
    }
  },
  setup(props, _ref) {
    let {
      emit
    } = _ref;
    const {
      feedOptions
    } = toRefs(props);
    const contentId = ref(uniqueId('stacked-layout-'));
    const wrapper = ref(null);
    const root = ref(null);
    const isOpen = ref(false);
    const wrapperHeight = ref(null);
    const btnHeight = ref(null);
    const wrapperOverflow = ref(null);
    const nodes = ref([]);
    const {
      backgroundColor: btnBgColor,
      color: btnColor
    } = useBtnColors({
      noTransparency: true
    });
    const {
      stop
    } = useIntersectionObserver(wrapper, _ref2 => {
      let [{
        isIntersecting
      }] = _ref2;
      if (isIntersecting) {
        stop();
        emit('event', EVENTS.IMPRESSION);
      }
    }, {
      rootMargin: '0px',
      threshold: 1
    });
    const classes = computed(() => {
      return {
        'v-stacked-layout--closed': !isOpen.value,
        [`v-stacked-layout--${nodes.value.length}`]: true
      };
    });
    const styles = computed(() => {
      const extras = {};
      // Remove alpha from btn background
      if (!feedOptions.value.disable_stacked_color && !isOpen.value) {
        extras[getCSSVariableName(PROFILE_PREFIX, 'btnBgColor')] = btnBgColor.value;
        extras[getCSSVariableName(PROFILE_PREFIX, 'btnColor')] = btnColor.value;
      }
      return {
        '--c-easing': Easing.EASE_IN_OUT,
        '--c-box-shadow-color': `rgba(${tinycolor(btnBgColor.value).isDark() ? '255,255,255' : '0,0,0'}, .15)`,
        '--c-btn-height': btnHeight.value,
        ...extras
      };
    });
    useResizeObserver(root, entries => {
      btnHeight.value = `${entries[0].contentRect.height}px`;
    });
    const innerStyles = computed(() => {
      return {
        height: wrapperHeight.value,
        overflow: wrapperOverflow.value
      };
    });
    watch(isOpen, async () => {
      wrapperHeight.value = `${getElementHeight(root.value)}px`;
      await nextTick();
      const elementHeights = nodes.value.map(node => getElementHeight(node));
      const sum = elementHeights.reduce((prev, cur) => prev + cur);
      wrapperOverflow.value = 'hidden';
      wrapperHeight.value = `${sum}px`;
      let top = 0;
      nodes.value.forEach((node, index) => {
        if (index > 0) {
          top = top + elementHeights[index - 1];
          node.style.transform = `translate(0, ${top}px)`;
        }
      });
      root.value.addEventListener('transitionend', transitionEndDebounced);
    });

    // Remove any styles that were added
    function transitionEnd() {
      var _nodes$value$0$queryS;
      root.value.removeEventListener('transitionend', transitionEndDebounced);
      nodes.value.forEach(node => {
        node.style.transform = 'none';
        node.style.position = 'static';
        node.style.transition = 'none';
      });
      wrapperOverflow.value = '';
      wrapperHeight.value = '';

      // Focus first link
      (_nodes$value$0$queryS = nodes.value[0].querySelector('a, button')) === null || _nodes$value$0$queryS === void 0 ? void 0 : _nodes$value$0$queryS.focus();
    }
    const transitionEndDebounced = debounce(transitionEnd, 100);
    function handleClick() {
      isOpen.value = !isOpen.value;
      emit('open');
    }
    onMounted(() => {
      nodes.value = Array.from(root.value.childNodes);
    });
    return {
      root,
      wrapper,
      contentId,
      isOpen,
      classes,
      styles,
      innerStyles,
      handleClick
    };
  }
};
export { script as default };